import { z } from 'zod';

const PortfolioItemBonusShare = z.object({
  quantity: z.number(),
  netAverage: z.number(),
  netAverageDPS: z.number(),
});

const FalconAlgorithmInfo = z.object({
  isSLTP: z.boolean(),
  algorithmId: z.number(),
  percent: z.number(),
  upperThreshold: z.number().nullable(),
  lowerThreshold: z.number().nullable(),
  upperTrigger: z.number().nullable(),
  lowerTrigger: z.number().nullable(),
  expirationDate: z.string().nullable(),
  createdOn: z.string().nullable(),
  executedQuantity: z.number().nullable(),
});

const PortfolioItem = z.object({
  id: z.string(),
  averagePrice: z.number(),
  netAveragePrice: z.number(),
  netAverageDps: z.number(),
  instrumentName: z.string().nullable(),
  lastTradePrice: z.number(),
  quantity: z.number(),
  buyTotalTradeVolume: z.number(),
  buyDeduction: z.number(),
  breakpoint: z.number(),
  dividend: z.number().nullable(),
  income: z.number(),
  salesTotalValue: z.number(),
  instrumentIsin: z.string().nullable(),
  referencePriceVariationPercentage: z.number(),
  instrumentStateCode: z.string().nullable(),
  instrumentTradingState: z.number(),
  instrumentStateTitle: z.string().nullable(),
  closingPrice: z.number().nullable(),
  theoricalOpeningPrice: z.number(),
  topVariationPercentage: z.number().nullable(),
  sectorCode: z.string().nullable(),
  stopLoss: z.number(),
  takeProfit: z.number(),
  instrumentTypeCode: z.string().nullable(),
  instrumentTitle: z.string().nullable(),
  subSectorCode: z.string().nullable(),
  isStopLost: z.boolean(),
  value: z.number().nullish(),
  returnValue: z.number().nullish(),
  returnValuePercentage: z.number().nullish(),
  bonusShare: PortfolioItemBonusShare.nullable(),
  falconAlgorithmInfo: FalconAlgorithmInfo.nullable(),
});

const PortfolioQuery = z.object({
  totalCount: z.number(),
  items: PortfolioItem.array().nullable(),
  resultState: z.number(),
});

const EmbeddedPutOptionSymbol = z.object({
  instrumentIsin: z.string().nullable(),
  instrumentName: z.string().nullable(),
  quantity: z.number(),
  clientCode: z.string().nullable(),
  nationalCode: z.string().nullable(),
  hasSellRequest: z.boolean(),
  insertDate: z.string().nullable(),
  presentationDateTime: z.string().nullable(),
  dueDate: z.string().nullable(),
  startDateOfAcceptanceOfTheRequest: z.string().nullable(),
  endDateOfAcceptanceOfTheRequest: z.string().nullable(),
});

const EmbeddedPutOptionSymbolsQuery = EmbeddedPutOptionSymbol.array();

const EmbeddedPutOptionSymbolInfoQuery = z.object({
  instrumentIsin: z.string().nullable(),
  instrumentName: z.string().nullable(),
  clientCode: z.string().nullable(),
  nationalCode: z.string().nullable(),
  quantity: z.number(),
  physicalRequestCount: z.number(),
  firstCashRequestCount: z.number(),
  physicalSettlementCount: z.number(),
  cashSettlementCount: z.number(),
  requestType: z.number(),
  insertDate: z.string(),
});

const EmbeddedPutOptionReport = z.object({
  instrumentIsin: z.string().nullable(),
  instrumentName: z.string().nullable(),
  clientCode: z.string().nullable(),
  nationalCode: z.string().nullable(),
  quantity: z.number(),
  physicalRequestCount: z.number(),
  firstCashRequestCount: z.number(),
  physicalSettlementCount: z.number(),
  cashSettlementCount: z.number(),
  requestType: z.number(),
  insertDate: z.string(),
});

const EmbeddedPutOptionReports = z.array(EmbeddedPutOptionReport);

const EmbeddedPutOptionRequestBody = z.object({
  isin: z.string().optional(),
  physicalRequestCount: z.number(),
  firstCashRequestCount: z.number(),
  requestType: z.number(),
  clientCode: z.string().optional(),
});

const VirtualPortfolioItem = z.object({
  id: z.number(),
  instrumentTitle: z.string().nullable(),
  averagePrice: z.number(),
  quantity: z.number(),
  lastTradePrice: z.number(),
  buyTotalTradeVolume: z.number(),
  instrumentName: z.string().nullable(),
  breakpoint: z.number(),
  income: z.number(),
  salesTotalValue: z.number(),
  instrumentIsin: z.string().nullable(),
  referencePriceVariationPercentage: z.number(),
  instrumentStateCode: z.string().nullable(),
  instrumentTradingState: z.number(),
  instrumentStateTitle: z.string().nullable(),
  closingPrice: z.number().nullable(),
  theoricalOpeningPrice: z.number(),
  topVariationPercentage: z.number().nullable(),
  sectorCode: z.string().nullable(),
  instrumentTypeCode: z.string().nullable(),
  subSectorCode: z.string().nullable(),
  value: z.number().nullish(),
  returnValue: z.number().nullish(),
  returnValuePercentage: z.number().nullish(),
  instrumentGroupId: z.string().nullable(),
});

const VirtualPortfolioQuery = z.object({
  totalCount: z.number(),
  items: VirtualPortfolioItem.array().nullable(),
});

const CreateVirtualPortfolioItemRequest = z.object({
  investorBourseCodeId: z.number().nullable(),
  instrumentIsin: z.string(),
  averagePrice: z.number(),
  quantity: z.number(),
});

const CreateVirtualPortfolioItemResponse = z.object({
  investorBourseCodeId: z.number().nullable(),
  instrumentIsin: z.string(),
  averagePrice: z.number(),
  quantity: z.number(),
  id: z.number(),
});

const UpdateVirtualPortfolioItemRequest = z.object({
  virtualAssetId: z.number(),
  price: z.number(),
  quantity: z.number(),
});

type PortfolioQuery = z.infer<typeof PortfolioQuery>;
type PortfolioItemBonusShare = z.infer<typeof PortfolioItemBonusShare>;
type CreateVirtualPortfolioItemRequest = z.infer<
  typeof CreateVirtualPortfolioItemRequest
>;
type CreateVirtualPortfolioItemResponse = z.infer<
  typeof CreateVirtualPortfolioItemResponse
>;
type UpdateVirtualPortfolioItemRequest = z.infer<
  typeof UpdateVirtualPortfolioItemRequest
>;
type VirtualPortfolioQuery = z.infer<typeof VirtualPortfolioQuery>;
type VirtualPortfolioItem = z.infer<typeof VirtualPortfolioItem>;
type PortfolioItem = z.infer<typeof PortfolioItem>;
type EmbeddedPutOptionSymbol = z.infer<typeof EmbeddedPutOptionSymbol>;
type EmbeddedPutOptionSymbolsQuery = z.infer<
  typeof EmbeddedPutOptionSymbolsQuery
>;
type EmbeddedPutOptionSymbolInfoQuery = z.infer<
  typeof EmbeddedPutOptionSymbolInfoQuery
>;
type EmbeddedPutOptionReport = z.infer<typeof EmbeddedPutOptionReport>;
type EmbeddedPutOptionReports = z.infer<typeof EmbeddedPutOptionReports>;
type EmbeddedPutOptionRequestBody = z.infer<
  typeof EmbeddedPutOptionRequestBody
>;

type FalconAlgorithmInfo = z.infer<typeof FalconAlgorithmInfo>;

export {
  PortfolioQuery,
  PortfolioItem,
  PortfolioItemBonusShare,
  EmbeddedPutOptionSymbol,
  EmbeddedPutOptionSymbolsQuery,
  EmbeddedPutOptionSymbolInfoQuery,
  EmbeddedPutOptionReports,
  EmbeddedPutOptionReport,
  EmbeddedPutOptionRequestBody,
  VirtualPortfolioQuery,
  VirtualPortfolioItem,
  CreateVirtualPortfolioItemRequest,
  UpdateVirtualPortfolioItemRequest,
  CreateVirtualPortfolioItemResponse,
  FalconAlgorithmInfo,
};
